import React, { useState, useEffect } from "react";
import companyService from "@/services/companyService";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue
} from "@/components/ui/select";
import { toast } from "sonner";

interface NIST80053State {
  enabled: "enabled" | "disabled";
}

const defaultNIST80053: NIST80053State = {
  enabled: "disabled",
};

export default function NIST80053Module() {
  const [NIST80053, setNIST80053] = useState<NIST80053State>(defaultNIST80053);
  const [NIST80053Initial, setNIST80053Initial] = useState<NIST80053State | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const fetchNIST80053 = async () => {
      setIsLoading(true);
      try {
        const settings = await companyService.getCompanySettings(undefined, "complianceModules");
        const nist80053Settings = settings?.["nist80053"] ?? {};
        // Precedence: 'Enabled' (case-insensitive), then 'active', 'enabled', 'Active', etc.
        let isEnabled = false;
        // Helper to check both string and boolean
        function checkEnabled(val: unknown): boolean {
          if (typeof val === 'boolean') return val;
          if (typeof val === 'string') return val.toLowerCase() === 'true' || val.toLowerCase() === 'enabled';
          return false;
        }
        if (typeof nist80053Settings.Enabled !== 'undefined') {
          isEnabled = checkEnabled(nist80053Settings.Enabled);
        } else if (typeof nist80053Settings.enabled !== 'undefined') {
          isEnabled = checkEnabled(nist80053Settings.enabled);
        } else if (typeof nist80053Settings.active !== 'undefined') {
          isEnabled = checkEnabled(nist80053Settings.active);
        } else if (typeof nist80053Settings.Active !== 'undefined') {
          isEnabled = checkEnabled(nist80053Settings.Active);
        }
        setNIST80053({ enabled: isEnabled ? "enabled" : "disabled" });
        setNIST80053Initial({ enabled: isEnabled ? "enabled" : "disabled" });
      } catch (err) {
        toast.error("Failed to fetch NIST 800-53 settings");
      } finally {
        setIsLoading(false);
      }
    };
    fetchNIST80053();
  }, []);

  const nist80053Changed = NIST80053Initial && JSON.stringify(NIST80053) !== JSON.stringify(NIST80053Initial);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await companyService.saveCompanySettings(undefined, "complianceModules", {
        nist80053: { Enabled: NIST80053.enabled === "enabled" },
      });
      setNIST80053Initial({ ...NIST80053 });
      toast.success("NIST 800-53 settings saved");
    } catch (err) {
      toast.error("Failed to save NIST 800-53 settings");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="bg-muted/30 p-6 rounded-lg border border-border space-y-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="bg-green-500/10 p-3 rounded-full">
          <span role="img" aria-label="NIST 800-53">🛡️</span>
        </div>
        <div>
          <div className="font-semibold text-lg">NIST 800-53</div>
          <div className="text-xs text-muted-foreground">Security and Privacy Controls</div>
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">Module Status</label>
        <Select
          value={NIST80053.enabled}
          onValueChange={val => setNIST80053(v => ({ ...v, enabled: val as NIST80053State["enabled"] }))}
          disabled={isLoading || isSaving}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="enabled">Enabled</SelectItem>
            <SelectItem value="disabled">Disabled</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="flex gap-2 mt-4">
        <Button onClick={handleSave} disabled={!nist80053Changed || isSaving || isLoading}>
          {isSaving ? "Saving..." : "Save NIST 800-53 Settings"}
        </Button>
      </div>
    </div>
  );
}
